import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import scrollIntoView from 'scroll-into-view';

import {
    pageContext,
    elementRulesStateToProps,
    getRulesFromState,
    get,
    applyVerticalStyles,
    getPathWithQueryString,
    sanitizeInnerHtml
} from 'ctm-util-functions';

import { formAddOnUIComponent } from 'ctm-css-themes';

import ConnectedInfoModal from '../../../modal/info_dialog/info_dialog';
import {
    isVisible,
    setVisibilityAction,
    shouldComponentUpdate as _shouldComponentUpdate
} from '../../utils';

// eslint-disable-next-line import/no-cycle
import { getSelectedValues, resolveTextTemplate } from '../../../utils';

import * as actions from '../../actions';
import { SimpleFlexbox } from '../../../flexbox/flexbox';
import { Anchor } from '../../../anchor/anchor';
import { LoadingAnimation } from '../../../loading_animation/loading_animation';

import styles from './field_row.css';
import { Notification } from '../../../notification/notification';

class _FieldRow extends Component {

    constructor(props) {
        super(props);
        this.formElementFactory = pageContext.getProps().factories.formElement;
        this.id = props.schema.identifier;
        this.styles = applyVerticalStyles(styles, 'field_row');
        // Set up static styles in constructor to avoid passing a new object for visualProps on each render
        this.visualProps = {
            elementContainer: props.schema.gridProps || {}
        };
        this.handleInitActions();
    }

    /**
     * Determine if the component should re-render based on the nextProps
     * @param  {Object} nextProps  The incoming set of props
     * @param  {Object} nextState  The incoming state
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        return _shouldComponentUpdate(nextProps, nextState, this.props, this.state);
    };

    /**
     * Set the visibility state of the field set when it mounts
     */
    componentDidMount = () => {
        const { dispatch, schema } = this.props;
        if (get(this.props, 'rules.visibility.result')) {
            dispatch(setVisibilityAction(this.id, schema.stateKey, isVisible(this.props)));
        }
        this.attachListenerToHTMLNode();
    };

    // @todo: document why this is needed
    /**
     * Hide FieldRows with visibility rules on unMount to prevent validation errors
     * Example: TBC
     */
    componentWillUnmount = () => {
        const { dispatch, schema } = this.props;
        if (get(this.props, 'rules.visibility')) {
            dispatch(actions.hideFieldSet(this.id, schema.stateKey));
        }
        this.detachListenerToHTMLNode();
    };

    /**
     * On update, if this field row is the first invalid field row (from the top of the page going down), scroll
     * the field row into view. Also, update the visibility state if the visibility rule result has changed since the
     * last update
     * @param {Object} prevProps - the previous set of props
     */
    componentDidUpdate = (prevProps) => {
        const { rules, dispatch, schema, touched, invalidElements, isModalOpen } = this.props;
        const validation = rules.validation;
        const firstInvalidElement = invalidElements.first() ? invalidElements.first().get('name') : null;
        const isFirst = firstInvalidElement === schema.identifier;

        if (touched && validation && !validation.result && isFirst) {
            scrollIntoView(this.node, {
                isScrollable: (target, defaultIsScrollable) => {
                    if (target === window && isModalOpen) {
                        return false;
                    }
                    return defaultIsScrollable(target);
                }
            });
        }

        if (get(this.props, 'rules.visibility')) {
            if (rules.visibility.result !== prevProps.rules.visibility.result) {
                // extra sanity check - only change visibility if it has changed
                dispatch(setVisibilityAction(this.id, schema.stateKey, isVisible(this.props)));
            }
        }
        this.attachListenerToHTMLNode();
    };

    attachListenerToHTMLNode = () => {
        if (this.subLabelWithHtmlNode) {
            const htmlNodeElem = this.getSubLabelHtmlNodeElement();
            if (htmlNodeElem && !this.listenerAttached) {
                htmlNodeElem.addEventListener('click', this.handleInfoModalClick);
                this.listenerAttached = true;
            }
        }
    };

    detachListenerToHTMLNode = () => {
        if (this.subLabelWithHtmlNode) {
            const htmlNodeElem = this.getSubLabelHtmlNodeElement();
            if (htmlNodeElem && this.listenerAttached) {
                htmlNodeElem.removeEventListener('click', this.handleInfoModalClick);
                this.listenerAttached = false;
            }
        }
    };

    getSubLabelHtmlNodeElement = () => {
        if (this.subLabelWithHtmlNode) {
            let dataId = '';
            for (const childNode of this.subLabelWithHtmlNode.childNodes) {
                if (childNode.dataset && childNode.dataset.id) {
                    dataId = childNode.dataset.id;
                    break;
                }
            }
            const dataIdSelector = `[data-id="${dataId}"]`;
            return this.subLabelWithHtmlNode.querySelector(dataIdSelector);
        }
        return null;
    };

    /**
     * Handle the init actions when the component mounts
     * This function is used to set state for a hidden form element so that it still gets posted in the backend
     */
    handleInitActions = () => {
        const { schema, dispatch } = this.props;
        const initActions = get(schema, 'onInitActions', []);
        initActions.forEach((action) => {
            if (action.initOnlyWhenSpecified === 'Y') {
                const { isSpecifiedInitField } = action;
                const isSpecifiedFieldToInit = resolveTextTemplate(isSpecifiedInitField, this.props);
                if (isSpecifiedFieldToInit === 'Y') {
                    dispatch(
                        actions.setFormValue(
                            action.identifier,
                            action.value,
                            action.stateKey
                        )
                    );
                }
            } else {
                dispatch(
                    actions.setFormValue(
                        action.identifier,
                        action.value,
                        action.stateKey
                    )
                );
            }
        });
    };

    /**
     * Render a label of the Field row.
     * @returns {Node} - label header row that may contain a toggle
     */
    getLabel = () => {
        // get flexbox config for label row
        const { schema } = this.props;
        const { className, visualProps } = this.getFieldRowHeaderFlexboxConfig();
        const label = this.getFormElement('label');
        return schema.label ? (
            <SimpleFlexbox className={className} {...visualProps}>
                <span>{label}</span>
                {schema.title ? null : this.getToggleElement('inLabel')}
            </SimpleFlexbox>
        ) : null;
    };

    /**
     * Render a sub label of the Field row.
     * @returns {Node} - sub label header row
     */
    getSubLabel = () => {
        const { schema, rules } = this.props;
        // get flexbox config for label row
        const { className, visualProps } = this.getFieldRowHeaderFlexboxConfig();

        const elements = get(schema, 'subLabel.elements');
        if (!elements) {
            const subLabel = this.getFormElement('subLabel');
            return (
                <SimpleFlexbox className={className} {...visualProps}>
                    <span>{subLabel}</span>
                </SimpleFlexbox>
            );
        }

        for (const element of elements) {
            const labelResultStr = [element.condition, '.result'].join('');
            // only one label is expected, so return the first one that meets the condition
            if (element.condition && get(rules, labelResultStr, true)) {
                if (element.subLabelWithHTML) {
                    const subLabelWithHTMLResolved = resolveTextTemplate(element.subLabelWithHTML, this.props);
                    const safeSubLabelWithHTML = sanitizeInnerHtml(subLabelWithHTMLResolved);
                    return (
                        <div style={{ display: 'block', marginBottom: '1rem' }}>
                            <span
                                dangerouslySetInnerHTML={{ __html: safeSubLabelWithHTML }} // eslint-disable-line react/no-danger
                            />
                        </div>
                    );
                }
                const subLabel = element.label ? resolveTextTemplate(element.label, this.props) : '';
                return (
                    <SimpleFlexbox className={className} {...visualProps}>
                        <span className={styles.sub_label}>{subLabel}</span>
                    </SimpleFlexbox>
                );
            }
        }
        return null;
    };

    /**
     * Render a sub label of the Field row for text with HTML.
     * @returns {Node} - sub label header row
     */
    getSubLabelWithHTML = () => {
        const { schema, rules } = this.props;
        // get flexbox config for label row
        const { className, visualProps } = this.getFieldRowHeaderFlexboxConfig();
        // Support conditional rendering - only show subLabelWithHTML when the specified controls rule result is true
        if (schema.subLabelWithHTMLCondition && !get(rules, `${schema.subLabelWithHTMLCondition}.result`, false)) {
            return null;
        }
        // Interpolate placeholders in subLabelWithHTML using resolveTextTemplate
        const subLabelWithHTML = schema.subLabelWithHTML ? resolveTextTemplate(schema.subLabelWithHTML, this.props) : '';
        const safeSubLabelWithHTML = sanitizeInnerHtml(subLabelWithHTML);
        return schema.subLabelWithHTML ? (
            <SimpleFlexbox className={className} {...visualProps}>
                <span
                    ref={(el) => { this.subLabelWithHtmlNode = el; }}
                    className={styles.sub_label_with_html}
                    data-id={schema.subLabelWithHTMLDataId}
                    dangerouslySetInnerHTML={{ __html: safeSubLabelWithHTML }} // eslint-disable-line react/no-danger
                />
            </SimpleFlexbox>
        ) : null;
    };

    /**
     * Render notification messages for the Field row.
     * @returns {Node} - notification message component
     */
    getNotificationMessage = () => {
        const { schema, rules } = this.props;
        const { className } = this.getFieldRowHeaderFlexboxConfig();
        const elements = get(schema, 'notificationMessage.elements');
        if (!elements) {
            return null;
        }
        for (const message of elements) {
            const messageResultStr = [message.condition, '.result'].join('');
            // only one notification message is expected, so return the first one that meets the condition
            if (message.condition && !get(rules, messageResultStr, true)) {
                // Interpolate selectors in content if present
                const resolvedContent = message.content ? resolveTextTemplate(message.content, this.props) : undefined;
                const notificationProps = {
                    content: resolvedContent,
                    convertTitleStrToHtmlNode: true,
                    convertContentStrToHtmlNode: true,
                    icon: true
                };
                if (message.header) {
                    notificationProps.title = message.header;
                }
                if (message.variant) {
                    notificationProps.variant = message.variant;
                }
                return (
                    <div className={className}>
                        <Notification {...notificationProps} />
                    </div>
                );
            }
        }
        return null;
    };

    /**
     * Get flexbox config for fieldRow header
     * @returns {Object} Flexbox config
     */
    getFieldRowHeaderFlexboxConfig = () => {
        // title|label row addon width style
        const addonWidthClass = get(this.props, 'schema.field.addonWidth', 'auto');

        return {
            className: formAddOnUIComponent[['addon_width_', addonWidthClass].join('')],
            visualProps: {
                row: true,
                gapless: 'xs',
                contentBetween: 'xs',
                itemsCenter: 'xs'
            }
        };
    };

    /**
     * Build the toggle element for the row
     * @param {String} position - An optional arg to specify if the toggle should be in the label (inLabel) or below the element
     * (belowElement). inLabel is the default.
     * @returns {Node} - toggle element that contains label and anchor
     */
    getToggleElement = (position = 'inLabel') => {
        const toggleElementSchemas = this.getChildElementSchemaByType('toggle');
        if (!toggleElementSchemas) {
            return null;
        }
        let toggleElement;
        for (const toggleElementSchema of toggleElementSchemas) {
            const { label, linkText, linkIcon, linkIconPosition, onClick, identifier, togglePosition = 'inLabel' } = toggleElementSchema;
            if (togglePosition !== position) {
                // don't render the toggle if the specified position doesn't match the configured position for the toggle
                continue;
            }

            // Check to see if there are rules on the toggleElement(s). Don't render the toggle if it has a visibility rule
            // and the result is false. If there are no visibility rule or the rule result is true, continue rendering the toggle.
            // This is useful when you want a toggle to change the visibility of an element outside of this fieldRow,
            // without having to specify two complete FieldRows that are the same except for the toggle functionality (eg, show and hide toggles)
            const visRule = get(get(this.props, 'toggleRules', {})[identifier], 'visibility');
            if (!get(visRule, 'result', true)) {
                continue;
            }

            const onAnchorClick = !toggleElementSchema.onClick && toggleElementSchema.redirect ? () => {
                event.preventDefault();
                const browserHistory = pageContext.getProps().browserHistory;
                browserHistory.push(getPathWithQueryString(toggleElementSchema.redirect));
            } : toggleElementSchema.onClick;

            const linkElement = (
                <Anchor
                    className={styles.toggle_span}
                    href="javascript:;"
                    id={identifier}
                    tabIndex={-1}
                    label={linkText}
                    iconPosition={linkIconPosition}
                    icon={linkIcon}
                    onClick={onAnchorClick}
                />
            );
            // Need to wrap in a span to be a flex item
            toggleElement = (
                <span className={this.styles.toggle_span}>
                    {label}
                    {' '}
                    {linkElement}
                </span>
            );
        }
        return toggleElement;
    };

    /**
     * Get the child element schema based on type
     * @param {String} type - the type of the childElementSchema
     * @returns {Object} - the filtered childElementSchema
     */
    getChildElementSchemaByType = (type) => {
        const { schema } = this.props;
        return schema.elements && schema.elements.filter((element) => {
            return element.type === type;
        });
    };

    /**
     * Return a fully constructed form element.
     * If elementType is supplied, an element of that type is returned, otherwise the description from
     * this.props.schema is used.
     * @param {String|null} elementType - An optional element type to force creation of a specific element.
     * @returns {Node} - The constructed element.
     */
    getFormElement = (elementType = null) => {
        const {
            schema,
            rules,
            touched,
            dispatch,
            value,
            selectedValues,
            extraProps,
            invalidElements
        } = this.props;
        return this.formElementFactory.createElement(
            schema,
            rules,
            touched,
            dispatch,
            value,
            selectedValues,
            elementType,
            { ...extraProps, invalidElements }
        );
    };

    /**
     * Return a LoadingAnimation component if this.props.showLoadingAnimation is true
     * This is currently only used by the ABN/ACN field as it has to performa an API call in order to validate the input
     * @returns {Node|null} - The LoadingAnimation component or null if this.props.showLoadingAnimation is false
     */
    getLoadingAnimation = () => {
        const { showLoadingAnimation } = this.props;
        if (showLoadingAnimation) {
            return <LoadingAnimation />;
        }
        return null;
    };

    /**
     * Get the FieldRow className
     * @returns {String} FieldRow className.
     */
    getContainerClassName = () => {
        const { schema, className } = this.props;
        const { cssClass, privateData } = schema;
        const schemaCssClass = cssClass ? styles[cssClass] : false;
        return [schemaCssClass || className || this.styles.field_row_container, privateData ? ' data-hj-suppress' : ''].join('');
    };

    /**
     * Renders the flexbox field row
     * @returns {Node} The flexbox row
     */
    renderRow = () => {
        if (!isVisible(this.props)) {
            return null;
        }

        const { schema, isInfoModalOpen, infoModalKey } = this.props;
        const { privateData } = schema;

        const validationIndicator = this.getFormElement('validationIndicator');
        let formElement = this.getFormElement();
        const className = this.getContainerClassName();
        const privateDataClass = privateData ? 'data-hj-suppress' : '';
        const validationRule = get(this.props, 'rules.validation');
        const cssRuleName = validationRule && !validationRule.result && this.styles.validation_rule_css ? this.styles.validation_rule_css : '';
        if (schema.validationIndicator) {
            formElement = (
                <div className={this.styles.field_row_with_validation_indicator}>
                    {formElement}
                    {validationIndicator}
                </div>
            );
        }

        return (
            <div className={[className, cssRuleName, privateDataClass].join(' ')}
                ref={(node) => {
                    this.node = node;
                }}>
                {this.getLabel()}
                {this.getSubLabel()}
                {this.getSubLabelWithHTML()}
                <SimpleFlexbox {...this.visualProps.elementContainer}>
                    {formElement}
                    {this.getLoadingAnimation()}
                </SimpleFlexbox>
                {get(this.props, 'schema.field.type') !== 'validationMessage' && (
                    <SimpleFlexbox>
                        {this.getFormElement('validationMessage')}
                    </SimpleFlexbox>
                )}
                {infoModalKey && (
                    <ConnectedInfoModal
                        isOpen={isInfoModalOpen}
                        closeModal={this.closeInfoModal}
                        infoModalKey={get(this.props, 'infoModalKey')}
                    />
                )}
                <SimpleFlexbox>
                    {this.getNotificationMessage()}
                </SimpleFlexbox>
                {this.getToggleElement('belowElement')}
            </div>
        );
    };

    /**
     * This is a placeholder method for now. eventually this will be smarter and return the formElement wrapped in an
     * appropriate container.
     *
     * Return a FieldCell containing the form element (for now just the form element).
     * @returns {Node} - The FieldCell (currently just the form element)
     */
    renderCell = () => {
        const { schema } = this.props;
        // use the css class as default if none has been specified
        const { cssClass = 'field_row_container' } = schema;
        // @todo: check this and cleanup, make a consistent way of creating a block or inline fieldRow
        return (
            <div className={this.styles[cssClass]}
                ref={(node) => { this.node = node; }}
            >
                {this.getLabel()}
                <SimpleFlexbox {...this.visualProps.elementContainer}>
                    {this.getFormElement()}
                    {this.getLoadingAnimation()}
                    {this.getFormElement('validationMessage')}
                </SimpleFlexbox>
            </div>
        );
    };

    /**
     * Render the appropriate form element container based on the layoutMode
     * @returns {Node} - The form element container.
     */
    render = () => {
        const { layoutMode } = this.props;
        if (!get(this.props, 'rules.visibility.result', true)) {
            return null;
        }
        // This is for preferences in energy currently where we have a row of field elements without a label beside it.
        if (layoutMode === 'row') {
            return this.renderCell();
        }
        return this.renderRow();
    };

    handleInfoModalClick = (event) => {
        const { dispatch, infoModalKey } = this.props;
        event.preventDefault();
        if (!infoModalKey) return;
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: ['helpers.', infoModalKey].join(''),
            value: { open: true }
        });
    };

    closeInfoModal = () => {
        const { dispatch, infoModalKey } = this.props;
        if (!infoModalKey) return;
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: ['helpers.', infoModalKey].join(''),
            value: { open: false }
        });
    };

}

_FieldRow.propTypes = {
    className: PropTypes.string, // The Classname passed from the FieldSet for this field row
    schema: PropTypes.shape({
        // identifier must be unique
        identifier: PropTypes.string.isRequired,
        // If you have two frontend fields that are different ways to set the same data to state, use backendIdentifier, and make a unique identifier
        backendIdentifier: PropTypes.string,
        // If you need to dynamically render a label based off some other value
        // e.g. if rendering car make selected in the label, use this pattern.
        selectors: PropTypes.shape({
            names: PropTypes.shape({
                someSelector: PropTypes.string // e.g. productCategory: selector:productCategory
            }),
            useDefaultValues: PropTypes.bool,
            props: PropTypes.shape({
                someSelector: PropTypes.shape({
                    // eslint-disable-next-line quote-props
                    'case': PropTypes.oneOf(['lower', 'upper', 'title']),
                    // eslint-disable-next-line quote-props
                    'default': PropTypes.string
                })
            })
        }),
        gridProps: PropTypes.object,             // Optional grid props for the container
        stateKey: PropTypes.string.isRequired,   // The stateKey that indicates which form this FieldRow is rendered in
        title: PropTypes.string,                 // Optional title for the FieldRow
        cssClass: PropTypes.oneOf([
            'form_inline_offset',
            'form_inline',
            'field_row_with_validation_indicator',
            'field_row_container',
            'field_row_container_center',
            'field_row_container_xsmall_margins',
            'no_margin',
            // @todo can we remove the shaped props check as some of the css is overridden in vertical but because of this check,
            // we have to add this line here
            'cell_benefit',
            'input_width'
        ]), // Optional css classname override
        validationIndicator: PropTypes.object,  // Optional ValidationIndicator object
        label: PropTypes.string,
        subLabel: PropTypes.string, // Optional subLabel for the field row
        subLabelWithHTML: PropTypes.string, // Optional subLabel that can contain trusted HTML for the field row
        subLabelWithHTMLCondition: PropTypes.string, // Optional controls rule identifier to conditionally show subLabelWithHTML
        subLabelWithHTMLDataId: PropTypes.string, // Optional data-id for subLabelWithHTML
        notificationMessage: PropTypes.object, // Optional warningMessage object for the field row
        initOnlyWhenSpecified: PropTypes.string,
        isSpecifiedInitField: PropTypes.string,
        elements: PropTypes.arrayOf(PropTypes.object),
        privateData: PropTypes.bool
    }).isRequired,            // The schema for this field row
    dispatch: PropTypes.func.isRequired,            // The dispatch function for firing actions - provided via mapStateToProps
    invalidElements: PropTypes.object.isRequired,   // The set of invalid form elements on the current page
    rules: PropTypes.object,                        // The set of evaluated rules required by this form group
    touched: PropTypes.bool,                        // Has this component been touched by the user
    layoutMode: PropTypes.oneOf(['row', 'column', 'multiColumns']), // row or column - default is column
    // All fields except MultiFieldAddress use string value.
    // @todo: review this when refactoring MFA
    value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
        PropTypes.number
    ]).isRequired,                                   // The current value of the form element in this FieldRow
    selectedValues: PropTypes.object,
    extraProps: PropTypes.object,                     // extraProps you want to pass down to all sub components, e.g. stateProps.product
    showLoadingAnimation: PropTypes.bool,
    isModalOpen: PropTypes.bool,
    isInfoModalOpen: PropTypes.bool,
    infoModalKey: PropTypes.string
};

/**
 * Add the required state attributes to props - touched, rules, invalidElements and value
 * @param {Object} state - the current application state
 * @param {Object} props - the incoming set of props
 * @returns {Object} - The state attrs to add to props.
 */
const mapStateToProps = (state, props) => {
    const id = props.schema.identifier;
    const valueID = props.schema.backendIdentifier || props.schema.identifier;
    const stateKey = props.schema.stateKey;
    const touchId = props.schema.touchIdentifier || id;
    // some custom FieldRows may need to override touched via props in some cases, eg ABN validation where we
    // want to show a loading icon while calling an API endpoint and only show vlidation once the call has finished
    const touched = props.touched !== undefined ? props.touched : state.getIn([...stateKey.split('.'), 'validation', 'touches', touchId]);

    const selectedValues = props.selectedValues || getSelectedValues(state, props);

    // @todo: this will go away once we can do EVT-2037
    const toggleRules = {};
    if (get(props.schema, 'elements')) {
        for (const childElement of props.schema.elements) {
            if (childElement.type === 'toggle') {
                const rules = getRulesFromState(state, { rules: childElement.rules });
                toggleRules[childElement.identifier] = getRulesFromState(state, { rules: childElement.rules });
            }
        }
    }
    let isInfoModalOpen = false;
    const dataId = props.schema && props.schema.subLabelWithHTMLDataId;
    if (dataId) {
        const modal = state.getIn(['vertical', 'quote', 'results', 'config', 'helpers', dataId]);
        isInfoModalOpen = modal && modal.open;
    }
    return {
        touched,
        selectedValues,
        invalidElements: state.getIn([...stateKey.split('.'), 'validation', 'invalidElements']),
        value: state.getIn([...stateKey.split('.'), 'form', ...valueID.split('.')]) || '',
        rules: elementRulesStateToProps()(state, props).rules,
        toggleRules,
        isModalOpen: state.getIn(['page', 'isModalOpen']),
        isInfoModalOpen: isInfoModalOpen || false,
        infoModalKey: props.schema && props.schema.infoModalKey
    };
};

export { _FieldRow };
export const FieldRow = connect(mapStateToProps)(_FieldRow);
